import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Observable } from 'rxjs';

import { WorkflowDevToolsService } from '@blaze-dashboard/editor-data-access';
import { PushNotificationsService, SwService } from '@blaze-runtime/pwa-feature';
import { UserInfo } from '@blaze-shared/api-contracts-domain';
import { fromRouter, ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { FeatureToggleService } from '@blaze-shared/platform-data-access';

import { APP_VERSION } from './app-version';

@Component({
  selector: 'blaze-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly swService = inject(SwService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  protected readonly pushNotificationsService = inject(PushNotificationsService);
  protected readonly user = signal<UserInfo | null>(null);
  protected displayActionBar$: Observable<boolean>;
  protected isAuthenticatedRoute$: Observable<boolean>;
  protected routerContainerClass$: Observable<string>;

  constructor() {
    inject(WorkflowDevToolsService).initialize();
    inject(FeatureToggleService).initialize();

    console.log(
      `%c⚡BLAZE ${APP_VERSION}`,
      'background: black; color: white; padding: 5px 30px 5px 25px;',
    );
    this.isAuthenticatedRoute$ = this.createIsAuthenticatedRouteObservable();
    this.displayActionBar$ = this.createDisplayActionBarObservable();
    this.routerContainerClass$ = this.isAuthenticatedRoute$.pipe(
      map((isAuthenticated) => {
        return isAuthenticated ? '' : 'blaze-not-authenticated';
      }),
    );
    this.router.events
      .pipe(
        filter((routerEvent) => {
          return (
            routerEvent instanceof NavigationEnd && !this.user() && routerEvent.url !== '/login'
          );
        }),
      )
      .subscribe(() => {
        this.user.set(this.projectContextFacade.userInfo || null);
      });
  }

  ngOnInit(): void {
    this.swService.registerServiceWorker();
  }

  ngAfterViewInit(): void {
    this.initializeBackgroundColor();
  }

  /**
   * Creates and observable which returns a boolean used to determine which layout to load.
   * Authenticated routes have a header/top nav. Non-authtenticated routes do not.
   */
  private createIsAuthenticatedRouteObservable(): Observable<boolean> {
    return this.store.select(fromRouter.selectUrl).pipe(
      filter((url) => !!url),
      map((url) => {
        return (
          !url.startsWith('/acceptinvite') &&
          !url.startsWith('/certlogin') &&
          !url.startsWith('/login') &&
          !url.startsWith('/preview') &&
          !url.startsWith('/resetpassword') &&
          !url.startsWith('/updatedevaccess')
        );
      }),
    );
  }

  private createDisplayActionBarObservable(): Observable<boolean> {
    const isOAuthCallbackRoute$ = this.store.select(fromRouter.selectUrl).pipe(
      filter((url) => !!url),
      map((url) => url.startsWith('/oauth/cb')),
    );

    return combineLatest([this.isAuthenticatedRoute$, isOAuthCallbackRoute$]).pipe(
      // Prevent showing action bar when user is not authenticated or is at oauth callback page.
      map(
        ([isAuthenticatedRoute, isOauthCallbackRoute]) =>
          isAuthenticatedRoute && !isOauthCallbackRoute,
      ),
    );
  }

  /**
   * Watches for the current route and adjusts the background color accordingly.
   * Some views have grey background, others white.
   */
  private initializeBackgroundColor(): void {
    this.store.select(fromRouter.selectUseGreyBackground).subscribe((useGreyBackground) => {
      // The grey background is the editor in edit section.
      this.document.body.classList.toggle('grey-background', useGreyBackground);
    });
  }
}
